import { Subject, Observable } from 'rxjs';

export class SessionExpiredError extends Error {}

export abstract class BaseAuthService {
    private _serverRootPath: string;
    private _accessToken: string | null;
    private _onSessionExpired = new Subject<void>();

    constructor(params: { serverRootPath: string; initialAccessToken: string | null }) {
        this._serverRootPath = params.serverRootPath;
        this._accessToken = params.initialAccessToken;
    }

    get serverRootPath(): string {
        return this._serverRootPath;
    }

    get onSessionExpired(): Observable<void> {
        return this._onSessionExpired.asObservable();
    }

    get accessToken(): string | null {
        return this._accessToken;
    }

    get isAuthenticated(): boolean {
        return this._accessToken !== null;
    }

    setAccessToken(token: string): void {
        this._accessToken = token;
    }

    clearAccessToken(): void {
        this._accessToken = null;
    }

    async authenticatedFetch(url: string, init: RequestInit = {}): Promise<Response> {
        if (!this._accessToken) {
            const success = await this._refreshAccessToken();
            if (!success) {
                this._onSessionExpired.next();
                throw new SessionExpiredError();
            }
        }

        let response = await this._fetchWithAuth(url, init);

        if (response.status === 401) {
            const success = await this._refreshAccessToken();
            if (!success) {
                this._onSessionExpired.next();
                throw new SessionExpiredError();
            }
            response = await this._fetchWithAuth(url, init);
        }

        if (response.status === 401) {
            this._onSessionExpired.next();
            throw new SessionExpiredError();
        }

        return response;
    }

    private async _fetchWithAuth(url: string, init: RequestInit): Promise<Response> {
        return await fetch(url, {
            ...init,
            credentials: 'include',
            headers: {
                ...init.headers,
                ...(this._accessToken && { Authorization: `Bearer ${this._accessToken}` }),
            },
        });
    }

    protected abstract _refreshAccessToken(): Promise<boolean>;
    protected abstract _logout(): Promise<void>;
}

export function delay(ms: number = 500): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
